import { Badge, Button, FileInput, Group, Paper, Select, Stack, Table, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type MetadataImport, useAdminMutation, useAdminQuery } from "../../api/admin";
import { api } from "../../api/client";
import { useCompanyOptions } from "../../api/hooks";
import { formatDateTime } from "../../lib/format";

interface ImportResult {
  importId: number;
  encoding: string;
  rowsTotal: number;
  rowsMatched: number;
  rowsUnmatched: number;
  rows: { line: number; regNumber: string; status: string; documentId?: number; note?: string }[];
}

export function ImportsPage() {
  const { t } = useTranslation();
  const list = useAdminQuery<{ items: MetadataImport[] }>("imports", "/api/imports?limit=30");
  const [file, setFile] = useState<File | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const companyOptions = useCompanyOptions(companyId);
  const [result, setResult] = useState<ImportResult | null>(null);
  const run = useAdminMutation(
    async (v: { file: File; companyId: string | null }) => {
      const form = new FormData();
      form.append("file", v.file, v.file.name);
      if (v.companyId) form.append("companyId", v.companyId);
      return api<ImportResult>("/api/imports/metadata-csv", { method: "POST", form });
    },
    ["imports"],
  );

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Stack gap="sm">
          <Text fw={600}>{t("admin.imports.title")}</Text>
          <Text size="sm" c="dimmed">
            {t("admin.imports.hint")}
          </Text>
          <Group align="flex-end" wrap="wrap">
            <FileInput
              label={t("admin.imports.file")}
              accept=".csv,text/csv"
              value={file}
              onChange={setFile}
              style={{ minWidth: 260 }}
            />
            <Select
              label={t("admin.imports.defaultCompany")}
              data={companyOptions}
              value={companyId}
              onChange={setCompanyId}
              clearable
              style={{ minWidth: 220 }}
            />
            <Button
              disabled={!file}
              loading={run.isPending}
              onClick={async () => {
                if (!file) return;
                try {
                  const r = await run.mutateAsync({ file, companyId });
                  setResult(r);
                  setFile(null);
                  notifications.show({
                    message: t("admin.imports.done", {
                      matched: r.rowsMatched,
                      total: r.rowsTotal,
                    }),
                    color: "green",
                  });
                } catch (e) {
                  notifications.show({ message: (e as Error).message, color: "red" });
                }
              }}
            >
              {t("admin.imports.run")}
            </Button>
          </Group>
          {result && (
            <Stack gap={4}>
              <Text size="sm">
                {t("admin.imports.summary", {
                  total: result.rowsTotal,
                  matched: result.rowsMatched,
                  unmatched: result.rowsUnmatched,
                  encoding: result.encoding,
                })}
              </Text>
              <Table verticalSpacing={2} withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>{t("document.regNumber")}</Table.Th>
                    <Table.Th>{t("upload.state")}</Table.Th>
                    <Table.Th>{t("admin.imports.note")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {result.rows.slice(0, 200).map((r) => (
                    <Table.Tr key={r.line}>
                      <Table.Td>{r.line}</Table.Td>
                      <Table.Td>{r.regNumber}</Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          variant="light"
                          color={
                            r.status === "matched"
                              ? "green"
                              : r.status === "skipped"
                                ? "gray"
                                : "orange"
                          }
                        >
                          {t(`admin.imports.status.${r.status}`, r.status)}
                        </Badge>
                        {r.documentId ? ` #${r.documentId}` : ""}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{r.note}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          )}
        </Stack>
      </Paper>
      <Stack gap="xs">
        <Text fw={600}>{t("admin.imports.history")}</Text>
        <Table.ScrollContainer minWidth={640}>
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("admin.imports.file")}</Table.Th>
                <Table.Th>{t("admin.imports.by")}</Table.Th>
                <Table.Th ta="right">{t("admin.imports.rows")}</Table.Th>
                <Table.Th ta="right">{t("admin.imports.matched")}</Table.Th>
                <Table.Th>{t("upload.added")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(list.data?.items ?? []).map((i) => (
                <Table.Tr key={i.id}>
                  <Table.Td>{i.filename}</Table.Td>
                  <Table.Td>{i.uploaded_by_name ?? ""}</Table.Td>
                  <Table.Td ta="right">{i.rows_total}</Table.Td>
                  <Table.Td ta="right">
                    {i.rows_matched} / {i.rows_unmatched}
                  </Table.Td>
                  <Table.Td>{formatDateTime(i.created_at)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    </Stack>
  );
}
